// Anveshan — Jobs v2 Dashboard (Phases A–F)
// Candidate + Employer dual mode. Shared Anveshan JWT login.
// Palette: navy #0F2846 · teal #0F8787 · ivory #F7F5F0 · ink #1A2432
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import {
  Briefcase, Buildings, Target, PaperPlaneRight, UserCircle, CheckCircle,
  Warning, X, GraduationCap, SlidersHorizontal, Plus, Trash,
  Download, LockKey, Flag, CalendarCheck, Clock, MapPin, Prohibit,
} from "@phosphor-icons/react";
import MultiImageUploader from "@/components/MultiImageUploader";

const NAVY = "#0F2846";
const TEAL = "#0F8787";
const IVORY = "#F7F5F0";
const INK = "#1A2432";

const label = (lang, en, hi) => (lang === "hi" ? hi : en);
const EDU_OPTS = ["10th", "12th", "Diploma", "Bachelor", "Master", "PhD"];
const WORK_TYPES = ["full-time", "part-time", "contract", "internship"];
const WORK_MODES = ["on-site", "remote", "hybrid"];
const AVAIL = ["immediate", "15-days", "30-days", "60-days"];
const JOB_STATUSES = ["draft", "published", "paused", "closed", "filled"];
const APP_STATUSES = ["applied", "under_review", "shortlisted", "interview", "offered", "hired", "rejected", "withdrawn"];

export default function Jobs() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [role, setRole] = useState("candidate");
  if (!user || user === false) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center" style={{ color: INK }}>
        <Briefcase size={40} weight="duotone" style={{ color: NAVY }} className="mx-auto" />
        <h1 className="font-heading text-5xl mt-3" style={{ color: NAVY }}>Anveshan Jobs</h1>
        <p className="mt-2 text-[#5C564D]">Please log in to use jobs & skill development.</p>
      </div>
    );
  }
  return (
    <div style={{ background: `linear-gradient(180deg, ${IVORY} 0%, #FFFFFF 100%)` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Header lang={lang} role={role} setRole={setRole} />
        <div className="mt-6">
          {role === "candidate" ? <CandidateDashboard lang={lang} /> : <EmployerDashboard lang={lang} />}
        </div>
      </div>
    </div>
  );
}

function Header({ lang, role, setRole }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4">
      <div>
        <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TEAL }}>
          {label(lang, "Anveshan Jobs & Skills", "अन्वेषण रोज़गार और कौशल")}
        </div>
        <h1 className="font-heading text-5xl mt-2 tracking-tight" style={{ color: NAVY }}>
          {label(lang, "Find work. Hire talent. Learn new skills.", "काम ढूँढें। प्रतिभा नियुक्त करें।")}
        </h1>
        <p className="mt-2 max-w-2xl text-[#5C564D]">
          {label(lang, "Two-way matching based on real skills. Private-by-default contact. Training to close your skill gaps.",
            "वास्तविक कौशल पर आधारित द्वि-दिशात्मक मिलान।")}
        </p>
      </div>
      <div className="rounded-full border p-1 bg-white flex" style={{ borderColor: `${NAVY}22` }}>
        {[["candidate", "I'm looking for work", "काम ढूँढ रहा/रही हूँ"], ["employer", "I'm hiring", "मैं भर्ती कर रहा/रही हूँ"]].map(([k, en, hi]) => (
          <button key={k} onClick={() => setRole(k)} data-testid={`role-${k}`}
            className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
            style={{ background: role === k ? NAVY : "transparent", color: role === k ? "white" : NAVY }}>
            {label(lang, en, hi)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================= CANDIDATE =============================
const CAND_TABS = [
  { k: "find",     en: "Find jobs",       hi: "नौकरियाँ" },
  { k: "matches",  en: "Recommended",     hi: "सुझाव" },
  { k: "apps",     en: "My applications", hi: "मेरे आवेदन" },
  { k: "invites",  en: "Invitations",     hi: "आमंत्रण" },
  { k: "portfolio",en: "My portfolio",    hi: "पोर्टफ़ोलियो" },
  { k: "skills",   en: "Skill gaps",      hi: "कौशल अंतर" },
  { k: "learn",    en: "Learning plan",   hi: "शिक्षा योजना" },
  { k: "settings", en: "Privacy",         hi: "निजता" },
];

function CandidateDashboard({ lang }) {
  const [tab, setTab] = useState("find");
  return (
    <>
      <TabBar tabs={CAND_TABS} tab={tab} setTab={setTab} lang={lang} testidPrefix="ctab" />
      <div className="mt-6">
        {tab === "find" && <FindJobsTab lang={lang} />}
        {tab === "matches" && <MatchesTab lang={lang} />}
        {tab === "apps" && <MyApplicationsTab lang={lang} />}
        {tab === "invites" && <InvitationsTab lang={lang} />}
        {tab === "portfolio" && <PortfolioTab lang={lang} />}
        {tab === "skills" && <SkillGapsTab lang={lang} />}
        {tab === "learn" && <LearningPlanTab lang={lang} />}
        {tab === "settings" && <CandidatePrivacyTab lang={lang} />}
      </div>
    </>
  );
}

function FindJobsTab({ lang }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", location: "", work_type: "", work_mode: "", skills: "", min_experience: "" });
  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) p[k] = ["min_experience", "salary_min"].includes(k) ? Number(v) : v; });
    return p;
  }, [filters]);
  useEffect(() => {
    setLoading(true);
    api.get("/jobs", { params }).then(({ data }) => setJobs(data))
      .catch(() => toast.error("Failed to load jobs"))
      .finally(() => setLoading(false));
  }, [params]);
  return (
    <>
      <Filters lang={lang} filters={filters} setFilters={setFilters} />
      <div className="mt-5">
        {loading ? <Loading /> : jobs.length === 0 ? <EmptyCard title={label(lang, "No published jobs", "कोई प्रकाशित नौकरी नहीं")} body={label(lang, "Try different filters.", "फ़िल्टर बदलें।")} /> : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="jobs-grid">
            {jobs.map((j) => <JobCard key={j.id} job={j} lang={lang} />)}
          </div>
        )}
      </div>
    </>
  );
}

function Filters({ lang, filters, setFilters }) {
  return (
    <div className="rounded-2xl bg-white border p-4 grid grid-cols-2 md:grid-cols-6 gap-2" style={{ borderColor: `${NAVY}22` }}>
      <input data-testid="f-q" placeholder={label(lang, "Keyword", "कीवर्ड")} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
      <input data-testid="f-location" placeholder={label(lang, "Location", "स्थान")} value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
      <select data-testid="f-work-type" value={filters.work_type} onChange={(e) => setFilters({ ...filters, work_type: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }}>
        <option value="">{label(lang, "Any work type", "कोई भी")}</option>
        {WORK_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <select data-testid="f-work-mode" value={filters.work_mode} onChange={(e) => setFilters({ ...filters, work_mode: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }}>
        <option value="">{label(lang, "Any mode", "कोई मोड")}</option>
        {WORK_MODES.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <input data-testid="f-skills" placeholder={label(lang, "Skills (csv)", "कौशल (अल्पविराम)")} value={filters.skills} onChange={(e) => setFilters({ ...filters, skills: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
      <input data-testid="f-min-exp" type="number" placeholder={label(lang, "Max exp req.", "अधिकतम अनुभव आवश्यकता")} value={filters.min_experience} onChange={(e) => setFilters({ ...filters, min_experience: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
    </div>
  );
}

function JobCard({ job, lang }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-white border p-5" style={{ borderColor: `${NAVY}22` }} data-testid={`job-card-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-xl truncate" style={{ color: NAVY }}>{job.title}</div>
          <div className="text-sm text-[#5C564D] flex items-center gap-2 mt-0.5"><Buildings size={13} /> {job.company} · <MapPin size={11} /> {job.location}</div>
        </div>
        {job.match && (
          <div className="text-right">
            <div className="text-xs font-bold" style={{ color: TEAL }}>{label(lang, "Match", "मेल")}</div>
            <div className="font-heading text-2xl" style={{ color: job.match.score >= 70 ? "#15803D" : job.match.score >= 40 ? TEAL : "#C2410C" }} data-testid={`job-score-${job.id}`}>{job.match.score}</div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-3">
        {(job.skills || job.required_skills || []).slice(0, 6).map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${TEAL}1a`, color: TEAL }}>{s}</span>)}
      </div>
      <div className="text-xs text-[#5C564D] mt-3 flex flex-wrap gap-3">
        {job.work_type && <span className="flex items-center gap-1"><Briefcase size={11} /> {job.work_type}</span>}
        {job.work_mode && <span>{job.work_mode}</span>}
        {(job.salary_min || job.salary_max) && <span>${job.salary_min || "?"}–${job.salary_max || "?"}</span>}
        {job.min_experience_years !== undefined && <span>{job.min_experience_years}+ yrs</span>}
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={() => setOpen(true)} data-testid={`btn-view-${job.id}`} className="rounded-full px-4 py-1.5 text-xs font-semibold text-white" style={{ background: NAVY }}>{label(lang, "View & apply", "देखें और आवेदन करें")}</button>
      </div>
      {open && <JobDetailModal job={job} lang={lang} onClose={() => setOpen(false)} />}
    </div>
  );
}

function JobDetailModal({ job, lang, onClose }) {
  const [detail, setDetail] = useState(job);
  const [open, setOpen] = useState(false);
  const [cover, setCover] = useState("");
  const [qual, setQual] = useState("");
  const [resume, setResume] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get(`/jobs/${job.id}`).then(({ data }) => setDetail(data)).catch((err) => console.error(err)); }, [job.id]);
  const apply = async () => {
    setSending(true);
    try {
      await api.post("/jobs/apply", { job_id: job.id, cover_letter: cover, user_qualification: qual, resume_url: resume || null });
      toast.success(label(lang, "Application sent", "आवेदन भेजा गया"));
      setOpen(false); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSending(false); }
  };
  const bd = detail.match?.breakdown || {};
  return (
    <Modal onClose={onClose} testid={`job-detail-${job.id}`}>
      <div className="font-heading text-3xl" style={{ color: NAVY }}>{detail.title}</div>
      <div className="text-sm text-[#5C564D]">{detail.company} · {detail.location}</div>
      {detail.match && (
        <div className="mt-3 p-3 rounded-xl border" style={{ borderColor: `${TEAL}44`, background: `${TEAL}0a` }} data-testid="job-match-breakdown">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold" style={{ color: NAVY }}>{label(lang, "Your compatibility", "आपकी अनुकूलता")}</div>
            <div className="font-heading text-2xl" style={{ color: TEAL }}>{detail.match.score}/100</div>
          </div>
          <div className="grid grid-cols-2 gap-1 mt-2 text-xs">
            {["skills", "experience", "education", "location", "salary", "preferences"].map((k) => {
              const r = bd[k] || { points: 0, weight: 0, note: "—" };
              return (
                <div key={k} className="flex justify-between" data-testid={`crit-${k}`}>
                  <span className="capitalize text-[#5C564D]">{k}</span>
                  <span className="font-semibold" style={{ color: INK }}>{r.points}/{r.weight}</span>
                </div>
              );
            })}
          </div>
          {bd.skills?.missing?.length ? (
            <div className="mt-2 text-xs text-[#5C564D]">
              <span className="font-bold" style={{ color: "#B91C1C" }}>{label(lang, "Missing: ", "लापता: ")}</span>
              {bd.skills.missing.join(", ")}
            </div>
          ) : null}
        </div>
      )}
      {detail.description && <p className="text-sm text-[#5C564D] mt-3">{detail.description}</p>}
      {detail.responsibilities && (<><div className="font-semibold text-sm mt-3" style={{ color: NAVY }}>{label(lang, "Responsibilities", "ज़िम्मेदारियाँ")}</div><p className="text-sm text-[#5C564D]">{detail.responsibilities}</p></>)}
      {detail.benefits?.length ? (<div className="mt-2 flex flex-wrap gap-1">{detail.benefits.map((b) => <span key={b} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F2EBE5]">{b}</span>)}</div>) : null}
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="btn-open-apply" className="mt-4 rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>
          {label(lang, "Apply now", "आवेदन करें")}
        </button>
      ) : (
        <div className="mt-4 space-y-2">
          <input data-testid="apply-qual" placeholder={label(lang, "Your qualification", "आपकी शिक्षा")} value={qual} onChange={(e) => setQual(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
          <textarea data-testid="apply-cover" rows={3} placeholder={label(lang, "Cover note", "कवर नोट")} value={cover} onChange={(e) => setCover(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
          <input data-testid="apply-resume" placeholder={label(lang, "Resume URL (optional)", "रिज़्यूमे URL")} value={resume} onChange={(e) => setResume(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
          <button onClick={apply} disabled={sending} data-testid="btn-send-application" className="w-full rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>{sending ? "…" : label(lang, "Send application", "आवेदन भेजें")}</button>
        </div>
      )}
    </Modal>
  );
}

function MatchesTab({ lang }) {
  const [jobs, setJobs] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/jobs/matches/mine").then(({ data }) => setJobs(data))
      .catch((e) => { setJobs([]); setErr(formatApiErrorDetail(e.response?.data?.detail) || "Build portfolio to see matches"); });
  }, []);
  if (jobs === null) return <Loading />;
  if (!jobs.length) return <EmptyCard title={label(lang, "No matches yet", "कोई मेल नहीं")} body={err || label(lang, "Fill in your portfolio and preferences.", "अपनी प्रोफ़ाइल भरें।")} />;
  return <div className="grid md:grid-cols-2 gap-4" data-testid="matches-grid">{jobs.map((j) => <JobCard key={j.id} job={j} lang={lang} />)}</div>;
}

function MyApplicationsTab({ lang }) {
  const [apps, setApps] = useState([]);
  const load = () => api.get("/jobs/applications/mine").then(({ data }) => setApps(data)).catch((err) => console.error(err));
  useEffect(() => { load(); }, []);
  const withdraw = async (id) => {
    try { await api.post(`/applications/${id}/withdraw`); toast.success("withdrawn"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const offerRespond = async (id, action) => {
    try { await api.post(`/applications/${id}/offer-respond`, { action }); toast.success(action); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (!apps.length) return <EmptyCard title={label(lang, "No applications yet", "कोई आवेदन नहीं")} body={label(lang, "Apply to a job from the Find jobs tab.", "नौकरी के लिए आवेदन करें।")} />;
  return (
    <div className="space-y-3" data-testid="my-apps">
      {apps.map((a) => (
        <div key={a.id} className="rounded-2xl bg-white border p-4" style={{ borderColor: `${NAVY}22` }} data-testid={`app-row-${a.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-heading text-lg" style={{ color: NAVY }}>{a.job_title}</div>
              <div className="text-xs text-[#5C564D]">Applied {new Date(a.created_at).toLocaleDateString()}</div>
            </div>
            <StatusPill status={a.status || "applied"} />
          </div>
          {a.status === "interview" && a.interview_at && (
            <div className="mt-2 text-xs text-[#5C564D]"><CalendarCheck size={12} className="inline mr-1" /> {new Date(a.interview_at).toLocaleString()} · {a.interview_venue || a.interview_link}</div>
          )}
          {a.status === "offered" && a.offer && (
            <div className="mt-2 p-2 rounded-xl border" style={{ borderColor: `${TEAL}55`, background: `${TEAL}0a` }}>
              <div className="text-xs font-semibold" style={{ color: NAVY }}>{label(lang, "Offer received", "ऑफर प्राप्त")}: ${a.offer.salary || "—"} · Joining {a.offer.joining || "—"}</div>
              <div className="mt-1 flex gap-2">
                <button onClick={() => offerRespond(a.id, "accept")} data-testid={`accept-offer-${a.id}`} className="text-xs rounded-full px-3 py-1 text-white" style={{ background: TEAL }}>{label(lang, "Accept", "स्वीकार")}</button>
                <button onClick={() => offerRespond(a.id, "decline")} data-testid={`decline-offer-${a.id}`} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: "#B91C1C", color: "#B91C1C" }}>{label(lang, "Decline", "अस्वीकार")}</button>
              </div>
            </div>
          )}
          {["applied", "under_review", "shortlisted", "interview"].includes(a.status) && (
            <button onClick={() => withdraw(a.id)} data-testid={`withdraw-${a.id}`} className="mt-2 text-xs rounded-full px-3 py-1 border" style={{ borderColor: "#B91C1C", color: "#B91C1C" }}>{label(lang, "Withdraw", "वापस लें")}</button>
          )}
        </div>
      ))}
    </div>
  );
}

function InvitationsTab({ lang }) {
  const [invs, setInvs] = useState([]);
  const load = () => api.get("/invitations/mine").then(({ data }) => setInvs(data)).catch((err) => console.error(err));
  useEffect(() => { load(); }, []);
  const respond = async (id, action) => {
    try { await api.post(`/invitations/${id}/respond`, { action }); toast.success(action); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (!invs.length) return <EmptyCard title={label(lang, "No invitations", "कोई आमंत्रण नहीं")} body={label(lang, "Employers can invite you to apply once your profile is active.", "सक्रिय प्रोफ़ाइल पर आमंत्रण मिलते हैं।")} />;
  return (
    <div className="space-y-3" data-testid="invites-list">
      {invs.map((i) => (
        <div key={i.id} className="rounded-2xl bg-white border p-4" style={{ borderColor: `${NAVY}22` }} data-testid={`inv-${i.id}`}>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="font-heading text-lg" style={{ color: NAVY }}>{i.job_title}</div>
              <div className="text-xs text-[#5C564D]">{i.company}</div>
              {i.message && <p className="text-xs mt-1 italic text-[#5C564D]">"{i.message}"</p>}
            </div>
            <StatusPill status={i.status} />
          </div>
          {i.status === "sent" && (
            <div className="mt-2 flex gap-2">
              <button onClick={() => respond(i.id, "accept")} data-testid={`inv-accept-${i.id}`} className="text-xs rounded-full px-3 py-1 text-white" style={{ background: TEAL }}>{label(lang, "Accept", "स्वीकार")}</button>
              <button onClick={() => respond(i.id, "decline")} data-testid={`inv-decline-${i.id}`} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: "#B91C1C", color: "#B91C1C" }}>{label(lang, "Decline", "अस्वीकार")}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PortfolioTab({ lang }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/candidate/portfolio");
        setForm({
          full_name: "", photo_url: "", city: "", district: "", state: "",
          phone: "", phone_visible: false, email_visible: false, profession: "", headline: "", bio: "",
          total_experience_years: 0, education: [], experience: [], skills: [],
          certifications: [], projects: [], portfolio_links: [],
          preferred_categories: [], preferred_locations: [],
          expected_salary_min: "", expected_salary_max: "", work_types: [], work_mode_pref: "", availability: "",
          preferred_industries: [], languages: [], resume_url: "", visibility: "active",
          ...(data || {}),
        });
      } catch (err) { console.error(err); setForm({}); }
    })();
  }, []);
  if (!form) return <Loading />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form,
      total_experience_years: Number(form.total_experience_years) || 0,
      expected_salary_min: numOrNull(form.expected_salary_min),
      expected_salary_max: numOrNull(form.expected_salary_max),
      skills: Array.isArray(form.skills) ? form.skills : splitCsv(form.skills),
      languages: Array.isArray(form.languages) ? form.languages : splitCsv(form.languages),
      preferred_categories: Array.isArray(form.preferred_categories) ? form.preferred_categories : splitCsv(form.preferred_categories),
      preferred_locations: Array.isArray(form.preferred_locations) ? form.preferred_locations : splitCsv(form.preferred_locations),
      preferred_industries: Array.isArray(form.preferred_industries) ? form.preferred_industries : splitCsv(form.preferred_industries),
      portfolio_links: Array.isArray(form.portfolio_links) ? form.portfolio_links : splitCsv(form.portfolio_links),
    };
    try { await api.put("/candidate/portfolio", payload); toast.success(label(lang, "Portfolio saved", "पोर्टफ़ोलियो सहेजा गया")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const setVisibility = async (v) => {
    try { await api.post("/candidate/visibility", { visibility: v }); set("visibility", v); toast.success(v); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <form onSubmit={save} className="space-y-5" data-testid="portfolio-form">
      <SectionCard title={label(lang, "Visibility", "दृश्यता")}>
        <div className="flex gap-2 flex-wrap">
          {["active", "not_looking", "private"].map((v) => (
            <button type="button" key={v} onClick={() => setVisibility(v)} data-testid={`vis-${v}`}
              className="rounded-full px-4 py-2 text-xs font-semibold border"
              style={{ background: form.visibility === v ? NAVY : "white", color: form.visibility === v ? "white" : NAVY, borderColor: NAVY }}>
              {v}
            </button>
          ))}
        </div>
      </SectionCard>
      <SectionCard title={label(lang, "Basic details", "मूल विवरण")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="p-name" lab={label(lang, "Full name", "पूरा नाम")} val={form.full_name} on={(v) => set("full_name", v)} required />
          <Input testid="p-prof" lab={label(lang, "Profession", "व्यवसाय")} val={form.profession} on={(v) => set("profession", v)} />
          <Input testid="p-headline" lab={label(lang, "Headline", "शीर्षक")} val={form.headline} on={(v) => set("headline", v)} />
          <Input testid="p-exp-years" type="number" lab={label(lang, "Total experience (years)", "कुल अनुभव")} val={form.total_experience_years} on={(v) => set("total_experience_years", v)} />
          <Input testid="p-city" lab={label(lang, "City", "शहर")} val={form.city} on={(v) => set("city", v)} />
          <Input testid="p-state" lab={label(lang, "State", "राज्य")} val={form.state} on={(v) => set("state", v)} />
          <Input testid="p-phone" lab={label(lang, "Phone", "फ़ोन")} val={form.phone} on={(v) => set("phone", v)} />
          <label className="flex items-center gap-2 text-xs mt-6">
            <input type="checkbox" data-testid="p-phone-vis" checked={!!form.phone_visible} onChange={(e) => set("phone_visible", e.target.checked)} /> {label(lang, "Show phone after shortlist", "शॉर्टलिस्ट के बाद फ़ोन दिखाएँ")}
          </label>
          <TextArea testid="p-bio" lab={label(lang, "Short bio", "संक्षिप्त परिचय")} val={form.bio} on={(v) => set("bio", v)} rows={3} />
        </div>
      </SectionCard>
      <SectionCard title={label(lang, "Education", "शिक्षा")}>
        <RepeatList items={form.education || []} setItems={(v) => set("education", v)} testid="edu"
          fields={[{ k: "degree", lab: "Degree", select: EDU_OPTS }, { k: "institution", lab: "Institution" }, { k: "year", lab: "Year" }, { k: "grade", lab: "Grade" }]} />
      </SectionCard>
      <SectionCard title={label(lang, "Experience", "अनुभव")}>
        <RepeatList items={form.experience || []} setItems={(v) => set("experience", v)} testid="exp"
          fields={[{ k: "title", lab: "Title" }, { k: "company", lab: "Company" }, { k: "from", lab: "From" }, { k: "to", lab: "To" }, { k: "description", lab: "Description", textarea: true }]} />
      </SectionCard>
      <SectionCard title={label(lang, "Skills, projects, certifications", "कौशल, परियोजनाएँ, प्रमाणपत्र")}>
        <CSV testid="p-skills" lab={label(lang, "Skills (comma-sep)", "कौशल")} val={(form.skills || []).join(", ")} on={(v) => set("skills", splitCsv(v))} />
        <CSV testid="p-langs" lab={label(lang, "Languages", "भाषाएँ")} val={(form.languages || []).join(", ")} on={(v) => set("languages", splitCsv(v))} />
        <RepeatList items={form.projects || []} setItems={(v) => set("projects", v)} testid="proj"
          fields={[{ k: "name", lab: "Name" }, { k: "link", lab: "Link" }, { k: "description", lab: "Description", textarea: true }]} />
        <RepeatList items={form.certifications || []} setItems={(v) => set("certifications", v)} testid="cert"
          fields={[{ k: "name", lab: "Name" }, { k: "issuer", lab: "Issuer" }, { k: "year", lab: "Year" }, { k: "url", lab: "URL" }]} />
        <CSV testid="p-links" lab={label(lang, "Portfolio links", "पोर्टफ़ोलियो लिंक")} val={(form.portfolio_links || []).join(", ")} on={(v) => set("portfolio_links", splitCsv(v))} />
      </SectionCard>
      <SectionCard title={label(lang, "Preferences", "पसंद")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <TagMulti lab={label(lang, "Work types", "कार्य प्रकार")} testid="p-wt" options={WORK_TYPES} value={form.work_types} onChange={(v) => set("work_types", v)} />
          <Select testid="p-wm" lab={label(lang, "Work mode", "कार्य मोड")} val={form.work_mode_pref} on={(v) => set("work_mode_pref", v)} options={[["", "—"], ...WORK_MODES.map((v) => [v, v])]} />
          <Select testid="p-avail" lab={label(lang, "Availability", "उपलब्धता")} val={form.availability} on={(v) => set("availability", v)} options={[["", "—"], ...AVAIL.map((v) => [v, v])]} />
          <Input testid="p-sal-min" type="number" lab={label(lang, "Expected salary min (USD)", "अपेक्षित वेतन न्यूनतम")} val={form.expected_salary_min} on={(v) => set("expected_salary_min", v)} />
          <Input testid="p-sal-max" type="number" lab={label(lang, "Expected salary max", "अपेक्षित वेतन अधिकतम")} val={form.expected_salary_max} on={(v) => set("expected_salary_max", v)} />
          <CSV testid="p-cat" lab={label(lang, "Preferred categories (csv)", "पसंदीदा श्रेणियाँ")} val={(form.preferred_categories || []).join(", ")} on={(v) => set("preferred_categories", splitCsv(v))} />
          <CSV testid="p-locs" lab={label(lang, "Preferred locations (csv)", "पसंदीदा स्थान")} val={(form.preferred_locations || []).join(", ")} on={(v) => set("preferred_locations", splitCsv(v))} />
          <CSV testid="p-ind" lab={label(lang, "Preferred industries (csv)", "पसंदीदा उद्योग")} val={(form.preferred_industries || []).join(", ")} on={(v) => set("preferred_industries", splitCsv(v))} />
        </div>
      </SectionCard>
      <SectionCard title={label(lang, "Resume", "रिज़्यूमे")}>
        <Input testid="p-resume" lab={label(lang, "Resume URL (private — shared only after mutual interest)", "रिज़्यूमे URL (निजी)")} val={form.resume_url} on={(v) => set("resume_url", v)} />
      </SectionCard>
      <div className="flex justify-end">
        <button type="submit" data-testid="btn-save-portfolio" className="rounded-full px-6 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>
          {label(lang, "Save portfolio", "सहेजें")}
        </button>
      </div>
    </form>
  );
}

function SkillGapsTab({ lang }) {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/jobs").then(({ data }) => setJobs(data.slice(0, 20))).catch((err) => console.error(err)); }, []);
  useEffect(() => {
    if (!selected) return;
    api.get(`/candidate/skill-gaps/${selected}`).then(({ data }) => setResult(data))
      .catch((e) => { setResult(null); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); });
  }, [selected]);
  const addToPlan = async (training_id, reason) => {
    try { await api.post("/candidate/learning-plan", { training_id, reason }); toast.success(label(lang, "Added to plan", "योजना में जोड़ा गया")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4">
      <div className="rounded-2xl bg-white border max-h-[70vh] overflow-y-auto" style={{ borderColor: `${NAVY}22` }} data-testid="skill-job-list">
        {jobs.map((j) => (
          <button key={j.id} onClick={() => setSelected(j.id)} data-testid={`skill-job-${j.id}`}
            className="w-full text-left px-3 py-2.5 border-b flex items-start gap-2" style={{ borderColor: "#F0E6D7", background: selected === j.id ? `${TEAL}10` : "white" }}>
            <Target size={14} style={{ color: TEAL }} className="mt-1" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: NAVY }}>{j.title}</div>
              <div className="text-[11px] text-[#5C564D] truncate">{j.company}</div>
            </div>
          </button>
        ))}
      </div>
      <div className="rounded-2xl bg-white border p-5" style={{ borderColor: `${NAVY}22` }}>
        {!selected ? <div className="text-sm text-[#5C564D]">{label(lang, "Pick a job to see your skill gaps.", "कौशल अंतर देखने के लिए नौकरी चुनें।")}</div>
         : !result ? <Loading />
         : (
          <div data-testid="skill-gap-result">
            <div className="flex items-center justify-between">
              <div className="font-heading text-xl" style={{ color: NAVY }}>{label(lang, "Skills breakdown", "कौशल विश्लेषण")}</div>
              <div className="font-heading text-2xl" style={{ color: TEAL }}>{result.match.score}/100</div>
            </div>
            <div className="mt-2 text-sm">
              <div><span className="font-semibold" style={{ color: "#15803D" }}>{label(lang, "Matched: ", "मेल: ")}</span>{(result.match.breakdown.skills.matched || []).join(", ") || "—"}</div>
              <div className="mt-1"><span className="font-semibold" style={{ color: "#B91C1C" }}>{label(lang, "Missing: ", "लापता: ")}</span>{(result.missing_skills || []).join(", ") || "—"}</div>
            </div>
            <div className="mt-4">
              <div className="text-sm font-bold" style={{ color: NAVY }}>{label(lang, "Recommended training (real courses)", "सुझावित कोर्स")}</div>
              {!result.recommended_training.length ? <div className="text-xs text-[#5C564D] mt-2">{label(lang, "No matching courses right now.", "कोई मेल खाता कोर्स नहीं।")}</div>
                : (
                <div className="space-y-2 mt-2">
                  {result.recommended_training.map((rec) => (
                    <div key={rec.program.id} className="rounded-xl border p-3 flex items-start gap-3" style={{ borderColor: `${TEAL}44` }} data-testid={`rec-${rec.program.id}`}>
                      <GraduationCap size={28} style={{ color: TEAL }} />
                      <div className="flex-1">
                        <div className="font-semibold text-sm" style={{ color: NAVY }}>{rec.program.title}</div>
                        <div className="text-[11px] text-[#5C564D]">{rec.program.category} · {rec.program.duration_weeks} weeks · {rec.program.level}</div>
                        <div className="text-[11px] mt-1">{label(lang, "Covers: ", "कवर: ")} <span style={{ color: TEAL }}>{rec.covers.join(", ")}</span></div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <a href={`/training`} className="text-[11px] rounded-full px-3 py-1 text-white text-center" style={{ background: NAVY }} data-testid={`view-${rec.program.id}`}>{label(lang, "View", "देखें")}</a>
                        <button onClick={() => addToPlan(rec.program.id, `gap for ${selected}`)} data-testid={`addplan-${rec.program.id}`} className="text-[11px] rounded-full px-3 py-1 border" style={{ borderColor: TEAL, color: TEAL }}>{label(lang, "Add to plan", "योजना में")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LearningPlanTab({ lang }) {
  const [plan, setPlan] = useState([]);
  useEffect(() => { api.get("/candidate/learning-plan").then(({ data }) => setPlan(data)).catch((err) => console.error(err)); }, []);
  if (!plan.length) return <EmptyCard title={label(lang, "No learning plan yet", "कोई योजना नहीं")} body={label(lang, "Add courses from the Skill gaps tab.", "कौशल अंतर टैब से कोर्स जोड़ें।")} />;
  return (
    <div className="space-y-3" data-testid="plan-list">
      {plan.map((p) => (
        <div key={p.id} className="rounded-2xl bg-white border p-4" style={{ borderColor: `${NAVY}22` }} data-testid={`plan-${p.training_id}`}>
          <div className="font-heading text-lg" style={{ color: NAVY }}>{p.program?.title || p.training_id}</div>
          <div className="text-xs text-[#5C564D]">{p.program?.category} · {p.program?.duration_weeks} wks</div>
          {p.reason && <div className="text-[11px] mt-1 text-[#5C564D]">{p.reason}</div>}
          <a href="/training" className="mt-2 inline-block text-xs rounded-full px-3 py-1 text-white" style={{ background: NAVY }}>{label(lang, "Open course", "कोर्स खोलें")}</a>
        </div>
      ))}
    </div>
  );
}

function CandidatePrivacyTab({ lang }) {
  const [blocks, setBlocks] = useState([]);
  useEffect(() => { api.get("/jobs/blocks").then(({ data }) => setBlocks(data)).catch((err) => console.error(err)); }, []);
  const exportData = async () => {
    try {
      const { data } = await api.get("/candidate/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `anveshan-jobs-export-${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm(label(lang, "Delete all your job-side data? This cannot be undone.", "क्या सारा डेटा हटाएँ?"))) return;
    try { await api.delete("/candidate/profile"); toast.success(label(lang, "Deleted", "हटाया गया")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const unblock = async (uid) => {
    try { await api.post("/jobs/unblock", { employer_user_id: uid }); toast.success("Unblocked"); setBlocks(blocks.filter((b) => b.blocked_user_id !== uid)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <SectionCard title={label(lang, "Your data", "आपका डेटा")}>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportData} data-testid="btn-cand-export" className="rounded-full px-4 py-2 text-sm font-semibold border" style={{ borderColor: NAVY, color: NAVY }}><Download size={14} className="inline mr-1" /> {label(lang, "Export my data", "डेटा निर्यात")}</button>
          <button onClick={del} data-testid="btn-cand-delete" className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "#B91C1C" }}><Trash size={14} className="inline mr-1" /> {label(lang, "Delete profile", "प्रोफ़ाइल हटाएँ")}</button>
        </div>
      </SectionCard>
      <SectionCard title={label(lang, "Blocked recruiters", "अवरुद्ध भर्तीकर्ता")}>
        {!blocks.length ? <div className="text-xs text-[#5C564D]">{label(lang, "None.", "कोई नहीं")}</div> : (
          blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between text-sm py-1" data-testid={`cb-${b.blocked_user_id}`}>
              <span className="font-mono text-xs text-[#5C564D]">{b.blocked_user_id}</span>
              <button onClick={() => unblock(b.blocked_user_id)} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: NAVY, color: NAVY }}>Unblock</button>
            </div>
          ))
        )}
      </SectionCard>
    </div>
  );
}

// ============================= EMPLOYER =============================
const EMP_TABS = [
  { k: "company",   en: "Company profile",      hi: "कंपनी" },
  { k: "post",      en: "Post / manage jobs",   hi: "नौकरी पोस्ट" },
  { k: "apps",      en: "Received applications", hi: "आवेदन" },
  { k: "search",    en: "Search candidates",    hi: "उम्मीदवार खोजें" },
  { k: "invites",   en: "Sent invitations",     hi: "भेजे आमंत्रण" },
];

function EmployerDashboard({ lang }) {
  const [tab, setTab] = useState("company");
  const [profile, setProfile] = useState(null);
  useEffect(() => { api.get("/employer/profile").then(({ data }) => setProfile(data)).catch((err) => console.error(err)); }, []);
  return (
    <>
      <VerifyBanner profile={profile} lang={lang} />
      <TabBar tabs={EMP_TABS} tab={tab} setTab={setTab} lang={lang} testidPrefix="etab" />
      <div className="mt-6">
        {tab === "company" && <CompanyProfileTab lang={lang} onSave={setProfile} />}
        {tab === "post"    && <ManageJobsTab lang={lang} isVerified={profile?.verified_status === "verified"} />}
        {tab === "apps"    && <ReceivedAppsTab lang={lang} />}
        {tab === "search"  && <CandidateSearchTab lang={lang} isVerified={profile?.verified_status === "verified"} />}
        {tab === "invites" && <SentInvitationsTab lang={lang} />}
      </div>
    </>
  );
}

function VerifyBanner({ profile, lang }) {
  if (!profile) return null;
  const v = profile.verified_status;
  if (v === "verified") return null;
  const color = v === "rejected" ? "#B91C1C" : "#D97706";
  return (
    <div className="mt-4 rounded-xl p-3 text-sm" style={{ background: `${color}11`, color }} data-testid="verify-banner">
      <Warning size={14} weight="fill" className="inline mr-1" />
      {v === "rejected"
        ? label(lang, "Your employer profile was rejected. Update it to resubmit for verification.", "आपकी कंपनी अस्वीकृत है।")
        : label(lang, "Your employer profile is pending verification. You can create jobs as drafts; publishing is unlocked after an admin verifies you.", "आपकी कंपनी सत्यापन के लिए लंबित है।")}
    </div>
  );
}

function CompanyProfileTab({ lang, onSave }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    api.get("/employer/profile").then(({ data }) => setForm(data || { company_name: "", sector: "", description: "", location: "", website: "", contact_name: "", contact_phone: "", contact_email: "", logo_url: "", docs_url: "" })).catch((err) => console.error(err));
  }, []);
  if (!form) return <Loading />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/employer/profile", form); setForm(data); onSave?.(data); toast.success(label(lang, "Saved — pending verification", "सहेजा गया — सत्यापन लंबित")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="emp-form">
      <SectionCard title={label(lang, "Company", "कंपनी")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="e-name" lab={label(lang, "Company name", "कंपनी का नाम")} val={form.company_name} on={(v) => set("company_name", v)} required />
          <Input testid="e-sector" lab={label(lang, "Business sector", "क्षेत्र")} val={form.sector} on={(v) => set("sector", v)} />
          <Input testid="e-location" lab={label(lang, "Location", "स्थान")} val={form.location} on={(v) => set("location", v)} />
          <Input testid="e-website" lab={label(lang, "Website", "वेबसाइट")} val={form.website} on={(v) => set("website", v)} />
          <Input testid="e-contact-name" lab={label(lang, "Contact name", "संपर्क नाम")} val={form.contact_name} on={(v) => set("contact_name", v)} />
          <Input testid="e-contact-phone" lab={label(lang, "Contact phone", "फ़ोन")} val={form.contact_phone} on={(v) => set("contact_phone", v)} />
          <Input testid="e-contact-email" lab={label(lang, "Contact email", "ईमेल")} val={form.contact_email} on={(v) => set("contact_email", v)} />
          <TextArea testid="e-desc" lab={label(lang, "Description", "विवरण")} val={form.description} on={(v) => set("description", v)} rows={3} />
        </div>
      </SectionCard>
      <SectionCard title={label(lang, "Proof of organization (optional)", "संगठन का प्रमाण")}>
        <MultiImageUploader testid="e-docs" label="Verification documents (any registration proof)" max={3} value={form.docs_url ? [form.docs_url] : []} onChange={(urls) => set("docs_url", urls[0] || "")} />
        <p className="text-[11px] text-[#5C564D] mt-2">{label(lang, "Admins use this to verify your organisation. Not shown to candidates.", "व्यवस्थापक इसका उपयोग सत्यापन के लिए करते हैं।")}</p>
      </SectionCard>
      <div className="flex justify-end"><button type="submit" data-testid="btn-save-emp" className="rounded-full px-6 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>{label(lang, "Save", "सहेजें")}</button></div>
    </form>
  );
}

function ManageJobsTab({ lang, isVerified }) {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/jobs/mine").then(({ data }) => setJobs(data)).catch((err) => console.error(err));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.post(`/jobs/${id}/status`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <div className="text-sm text-[#5C564D]">{label(lang, "Create and manage your postings.", "अपनी पोस्ट्स बनाएँ और प्रबंधित करें।")}</div>
        <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="btn-new-job" className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>
          <Plus size={14} weight="bold" className="inline mr-1" /> {label(lang, "New job", "नई नौकरी")}
        </button>
      </div>
      {!jobs.length ? <EmptyCard title={label(lang, "No jobs posted yet", "कोई नौकरी नहीं")} body={label(lang, "Create a job to begin.", "शुरू करें।")} /> : (
        <div className="space-y-3" data-testid="manage-jobs">
          {jobs.map((j) => (
            <div key={j.id} className="rounded-2xl bg-white border p-4" style={{ borderColor: `${NAVY}22` }} data-testid={`mj-${j.id}`}>
              <div className="flex justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-heading text-lg" style={{ color: NAVY }}>{j.title}</div>
                  <div className="text-xs text-[#5C564D]">{j.company} · {j.location}</div>
                </div>
                <StatusPill status={j.status || "draft"} />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={() => { setEditing(j); setOpen(true); }} data-testid={`edit-${j.id}`} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: NAVY, color: NAVY }}>Edit</button>
                {j.status !== "published" && <button disabled={!isVerified} onClick={() => setStatus(j.id, "published")} data-testid={`pub-${j.id}`} className="text-xs rounded-full px-3 py-1 text-white disabled:opacity-50" style={{ background: TEAL }} title={isVerified ? "" : "Verification required to publish"}>Publish</button>}
                {j.status === "published" && <button onClick={() => setStatus(j.id, "paused")} data-testid={`pause-${j.id}`} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: "#D97706", color: "#D97706" }}>Pause</button>}
                {["published", "paused"].includes(j.status) && <button onClick={() => setStatus(j.id, "closed")} data-testid={`close-${j.id}`} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: "#B91C1C", color: "#B91C1C" }}>Close</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && <JobEditorModal lang={lang} job={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </>
  );
}

function JobEditorModal({ lang, job, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: "", company: "", location: "", experience: "", salary: "",
    qualification_required: "", description: "", skills: [],
    department: "", category: "", responsibilities: "", required_education: "",
    required_skills: [], preferred_skills: [], min_experience_years: 0,
    work_type: "full-time", work_mode: "on-site", vacancies: 1,
    salary_min: "", salary_max: "", benefits: [], deadline: "", selection_process: "",
    contact_visibility: "after-shortlist", status: "draft",
    ...(job || {}),
    skills: job?.skills || [], required_skills: job?.required_skills || [],
    preferred_skills: job?.preferred_skills || [], benefits: job?.benefits || [],
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form,
      min_experience_years: Number(form.min_experience_years) || 0,
      vacancies: Number(form.vacancies) || 1,
      salary_min: numOrNull(form.salary_min),
      salary_max: numOrNull(form.salary_max),
      skills: Array.isArray(form.skills) ? form.skills : splitCsv(form.skills),
      required_skills: Array.isArray(form.required_skills) ? form.required_skills : splitCsv(form.required_skills),
      preferred_skills: Array.isArray(form.preferred_skills) ? form.preferred_skills : splitCsv(form.preferred_skills),
      benefits: Array.isArray(form.benefits) ? form.benefits : splitCsv(form.benefits),
    };
    try {
      if (job?.id) {
        await api.put(`/jobs/${job.id}`, payload);
      } else {
        await api.post(`/jobs`, payload);
      }
      toast.success(label(lang, "Saved", "सहेजा गया"));
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <Modal onClose={onClose} testid="job-editor">
      <div className="font-heading text-2xl" style={{ color: NAVY }}>{job ? label(lang, "Edit job", "संपादित करें") : label(lang, "New job", "नई नौकरी")}</div>
      <form onSubmit={save} className="mt-4 space-y-3 max-h-[70vh] overflow-y-auto pr-2">
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="j-title" lab="Title" val={form.title} on={(v) => set("title", v)} required />
          <Input testid="j-company" lab="Company" val={form.company} on={(v) => set("company", v)} required />
          <Input testid="j-location" lab="Location" val={form.location} on={(v) => set("location", v)} required />
          <Input testid="j-dept" lab="Department" val={form.department} on={(v) => set("department", v)} />
          <Input testid="j-category" lab="Category" val={form.category} on={(v) => set("category", v)} />
          <Select testid="j-wtype" lab="Work type" val={form.work_type} on={(v) => set("work_type", v)} options={WORK_TYPES.map((v) => [v, v])} />
          <Select testid="j-wmode" lab="Work mode" val={form.work_mode} on={(v) => set("work_mode", v)} options={WORK_MODES.map((v) => [v, v])} />
          <Input testid="j-exp" lab="Experience (text)" val={form.experience} on={(v) => set("experience", v)} />
          <Input testid="j-min-exp" type="number" lab="Min experience (years)" val={form.min_experience_years} on={(v) => set("min_experience_years", v)} />
          <Input testid="j-salary" lab="Salary (text / display)" val={form.salary} on={(v) => set("salary", v)} />
          <Input testid="j-sal-min" type="number" lab="Salary min (USD)" val={form.salary_min} on={(v) => set("salary_min", v)} />
          <Input testid="j-sal-max" type="number" lab="Salary max (USD)" val={form.salary_max} on={(v) => set("salary_max", v)} />
          <Input testid="j-vacancies" type="number" lab="Vacancies" val={form.vacancies} on={(v) => set("vacancies", v)} />
          <Input testid="j-deadline" type="date" lab="Deadline" val={form.deadline?.split?.("T")?.[0] || form.deadline} on={(v) => set("deadline", v ? new Date(v).toISOString() : "")} />
          <Select testid="j-edu" lab="Required education" val={form.required_education || ""} on={(v) => set("required_education", v)} options={[["", "—"], ...EDU_OPTS.map((v) => [v.toLowerCase(), v])]} />
          <Input testid="j-qual" lab="Qualification (free text)" val={form.qualification_required} on={(v) => set("qualification_required", v)} />
        </div>
        <TextArea testid="j-desc" lab="Description" val={form.description} on={(v) => set("description", v)} rows={3} />
        <TextArea testid="j-resp" lab="Responsibilities" val={form.responsibilities} on={(v) => set("responsibilities", v)} rows={3} />
        <CSV testid="j-skills" lab="Required skills (comma-sep)" val={(form.required_skills || []).join(", ")} on={(v) => set("required_skills", splitCsv(v))} />
        <CSV testid="j-skills-pref" lab="Preferred skills (comma-sep)" val={(form.preferred_skills || []).join(", ")} on={(v) => set("preferred_skills", splitCsv(v))} />
        <CSV testid="j-benefits" lab="Benefits (comma-sep)" val={(form.benefits || []).join(", ")} on={(v) => set("benefits", splitCsv(v))} />
        <TextArea testid="j-select" lab="Selection process" val={form.selection_process || ""} on={(v) => set("selection_process", v)} rows={2} />
        <button type="submit" data-testid="btn-save-job" className="w-full rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>{label(lang, "Save", "सहेजें")}</button>
      </form>
    </Modal>
  );
}

function ReceivedAppsTab({ lang }) {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [apps, setApps] = useState([]);
  const [actionOn, setActionOn] = useState(null);
  useEffect(() => { api.get("/jobs/mine").then(({ data }) => setJobs(data)).catch((err) => console.error(err)); }, []);
  useEffect(() => {
    if (!selected) return;
    api.get(`/applications/for-job/${selected}`).then(({ data }) => setApps(data)).catch((err) => console.error(err));
  }, [selected]);
  const act = async (id, status, extra = {}) => {
    try { await api.post(`/applications/${id}/status`, { status, ...extra }); toast.success(status); const { data } = await api.get(`/applications/for-job/${selected}`); setApps(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-4">
      <div className="rounded-2xl bg-white border max-h-[70vh] overflow-y-auto" style={{ borderColor: `${NAVY}22` }} data-testid="emp-job-list">
        {jobs.map((j) => (
          <button key={j.id} onClick={() => setSelected(j.id)} data-testid={`emp-job-${j.id}`}
            className="w-full text-left px-3 py-2.5 border-b" style={{ borderColor: "#F0E6D7", background: selected === j.id ? `${NAVY}10` : "white" }}>
            <div className="text-sm font-semibold truncate" style={{ color: NAVY }}>{j.title}</div>
            <div className="text-[11px] text-[#5C564D] truncate">{j.status} · {j.location}</div>
          </button>
        ))}
      </div>
      <div className="rounded-2xl bg-white border p-5 min-h-[200px]" style={{ borderColor: `${NAVY}22` }}>
        {!selected ? <div className="text-sm text-[#5C564D]">{label(lang, "Pick a job to see applications.", "आवेदन देखने के लिए नौकरी चुनें।")}</div>
         : !apps.length ? <div className="text-sm text-[#5C564D]">{label(lang, "No applications yet.", "अभी तक कोई आवेदन नहीं।")}</div>
         : (
          <div className="space-y-3" data-testid="recv-apps">
            {apps.map((a) => (
              <div key={a.id} className="rounded-xl border p-3" style={{ borderColor: `${NAVY}22` }} data-testid={`emp-app-${a.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-semibold" style={{ color: NAVY }}>{a.applicant_name}</div>
                    <div className="text-xs text-[#5C564D]">{a.user_qualification}</div>
                    {a.match && <div className="text-[11px] mt-1" style={{ color: TEAL }}>Match {a.match.score}/100 · Missing: {(a.match.breakdown.skills.missing || []).slice(0, 3).join(", ") || "—"}</div>}
                    {a.contact_phone && <div className="text-[11px] text-[#5C564D] mt-0.5">{a.contact_phone}</div>}
                  </div>
                  <StatusPill status={a.status || "applied"} />
                </div>
                <p className="text-xs text-[#5C564D] mt-2 italic line-clamp-2">"{a.cover_letter}"</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  <ActionBtn testid={`shortlist-${a.id}`} on={() => act(a.id, "shortlisted")} label="Shortlist" color={TEAL} />
                  <ActionBtn testid={`review-${a.id}`} on={() => act(a.id, "under_review")} label="Under review" />
                  <ActionBtn testid={`interview-${a.id}`} on={() => setActionOn({ app: a, kind: "interview" })} label="Interview" />
                  <ActionBtn testid={`offer-${a.id}`} on={() => setActionOn({ app: a, kind: "offer" })} label="Offer" color={NAVY} />
                  <ActionBtn testid={`reject-${a.id}`} on={() => act(a.id, "rejected")} label="Reject" color="#B91C1C" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {actionOn && <InterviewOfferModal lang={lang} on={actionOn} onDone={() => setActionOn(null)} refresh={() => api.get(`/applications/for-job/${selected}`).then(({ data }) => setApps(data))} />}
    </div>
  );
}

function InterviewOfferModal({ lang, on, onDone, refresh }) {
  const { app, kind } = on;
  const [form, setForm] = useState({ interview_at: "", interview_venue: "", interview_link: "", offer_salary: "", offer_joining: "", note: "" });
  const submit = async (e) => {
    e.preventDefault();
    const status = kind === "interview" ? "interview" : "offered";
    const extra = kind === "interview"
      ? { interview_at: form.interview_at ? new Date(form.interview_at).toISOString() : null, interview_venue: form.interview_venue, interview_link: form.interview_link, note: form.note }
      : { offer_salary: numOrNull(form.offer_salary), offer_joining: form.offer_joining, note: form.note };
    try { await api.post(`/applications/${app.id}/status`, { status, ...extra }); toast.success(status); await refresh(); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <Modal onClose={onDone} testid={`${kind}-modal`}>
      <div className="font-heading text-2xl capitalize" style={{ color: NAVY }}>{kind}</div>
      <form onSubmit={submit} className="mt-4 space-y-2">
        {kind === "interview" ? (
          <>
            <input required type="datetime-local" data-testid="int-when" value={form.interview_at} onChange={(e) => setForm({ ...form, interview_at: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
            <input data-testid="int-venue" placeholder="Venue" value={form.interview_venue} onChange={(e) => setForm({ ...form, interview_venue: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
            <input data-testid="int-link" placeholder="Link (optional)" value={form.interview_link} onChange={(e) => setForm({ ...form, interview_link: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
          </>
        ) : (
          <>
            <input type="number" required data-testid="off-salary" placeholder="Offered salary (USD)" value={form.offer_salary} onChange={(e) => setForm({ ...form, offer_salary: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
            <input type="date" data-testid="off-joining" placeholder="Joining date" value={form.offer_joining} onChange={(e) => setForm({ ...form, offer_joining: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
          </>
        )}
        <textarea rows={2} data-testid="io-note" placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
        <button data-testid={`${kind}-submit`} className="w-full rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>{label(lang, "Send", "भेजें")}</button>
      </form>
    </Modal>
  );
}

function CandidateSearchTab({ lang, isVerified }) {
  const [params, setParams] = useState({ skills: "", location: "", min_experience: "", job_id: "" });
  const [candidates, setCandidates] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  useEffect(() => { api.get("/jobs/mine").then(({ data }) => setMyJobs(data)).catch((err) => console.error(err)); }, []);
  const search = async () => {
    const q = {};
    Object.entries(params).forEach(([k, v]) => { if (v !== "") q[k] = ["min_experience"].includes(k) ? Number(v) : v; });
    try { const { data } = await api.get("/employer/candidates", { params: q }); setCandidates(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const invite = async (candidate_user_id) => {
    if (!params.job_id) { toast.error(label(lang, "Pick a job first", "पहले नौकरी चुनें")); return; }
    try { await api.post("/invitations", { candidate_user_id, job_id: params.job_id }); toast.success(label(lang, "Invited", "आमंत्रित")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (!isVerified) return <EmptyCard title={label(lang, "Verification required", "सत्यापन आवश्यक")} body={label(lang, "Verified employers only. Submit your company profile for admin review.", "सत्यापित भर्तीकर्ता ही उम्मीदवार खोज सकते हैं।")} />;
  return (
    <>
      <div className="rounded-2xl bg-white border p-4 grid grid-cols-2 md:grid-cols-5 gap-2" style={{ borderColor: `${NAVY}22` }}>
        <input data-testid="cs-skills" placeholder="Skills csv" value={params.skills} onChange={(e) => setParams({ ...params, skills: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
        <input data-testid="cs-location" placeholder="Location" value={params.location} onChange={(e) => setParams({ ...params, location: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
        <input data-testid="cs-min-exp" type="number" placeholder="Min experience" value={params.min_experience} onChange={(e) => setParams({ ...params, min_experience: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }} />
        <select data-testid="cs-job" value={params.job_id} onChange={(e) => setParams({ ...params, job_id: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: `${NAVY}22` }}>
          <option value="">Score vs. job…</option>
          {myJobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
      </div>
      <button onClick={search} data-testid="cs-search" className="mt-3 rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}>{label(lang, "Search", "खोजें")}</button>
      <div className="mt-4 grid md:grid-cols-2 gap-3" data-testid="candidates-grid">
        {candidates.map((c) => (
          <div key={c.id} className="rounded-2xl bg-white border p-4" style={{ borderColor: `${NAVY}22` }} data-testid={`cand-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-lg" style={{ color: NAVY }}>{c.full_name}</div>
                <div className="text-xs text-[#5C564D]">{c.profession || "—"} · {c.city || c.state || "—"} · {c.total_experience_years || 0} yrs</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(c.skills || []).slice(0, 6).map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${TEAL}1a`, color: TEAL }}>{s}</span>)}
                </div>
              </div>
              {c.match && <div className="font-heading text-xl" style={{ color: TEAL }}>{c.match.score}</div>}
            </div>
            <button onClick={() => invite(c.user_id)} data-testid={`invite-${c.user_id}`} className="mt-2 text-xs rounded-full px-3 py-1 text-white" style={{ background: NAVY }}>{label(lang, "Invite to apply", "आवेदन के लिए आमंत्रित")}</button>
          </div>
        ))}
      </div>
    </>
  );
}

function SentInvitationsTab({ lang }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/invitations/sent").then(({ data }) => setRows(data)).catch((err) => console.error(err)); }, []);
  if (!rows.length) return <EmptyCard title={label(lang, "No invitations sent", "कोई आमंत्रण नहीं भेजा")} body={label(lang, "Invite candidates from the Search tab.", "खोज टैब से आमंत्रित करें।")} />;
  return (
    <div className="space-y-3" data-testid="sent-invites">
      {rows.map((r) => (
        <div key={r.id} className="rounded-2xl bg-white border p-4 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: `${NAVY}22` }} data-testid={`si-${r.id}`}>
          <div>
            <div className="font-semibold" style={{ color: NAVY }}>{r.job_title}</div>
            <div className="text-xs text-[#5C564D]">To candidate: <span className="font-mono">{r.candidate_user_id}</span></div>
          </div>
          <StatusPill status={r.status} />
        </div>
      ))}
    </div>
  );
}

// ============================= Shared ============================
function TabBar({ tabs, tab, setTab, lang, testidPrefix }) {
  return (
    <div className="mt-6 flex gap-1 border-b flex-wrap" style={{ borderColor: `${NAVY}22` }} data-testid={`${testidPrefix}-bar`}>
      {tabs.map((t) => (
        <button key={t.k} onClick={() => setTab(t.k)} data-testid={`${testidPrefix}-${t.k}`}
          className="px-3 sm:px-4 py-2 text-sm font-semibold border-b-2 transition-colors"
          style={{ borderColor: tab === t.k ? NAVY : "transparent", color: tab === t.k ? NAVY : "#5C564D" }}>
          {label(lang, t.en, t.hi)}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    applied: "#5C564D", under_review: "#D97706", shortlisted: TEAL, interview: NAVY,
    offered: "#15803D", hired: "#15803D", rejected: "#B91C1C", withdrawn: "#5C564D",
    draft: "#5C564D", published: "#15803D", paused: "#D97706", closed: "#5C564D", filled: "#15803D",
    pending: "#D97706", verified: "#15803D", sent: "#D97706", accepted: "#15803D", declined: "#B91C1C",
  };
  const c = colors[status] || "#5C564D";
  return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${c}14`, color: c }} data-testid={`status-${status}`}>{(status || "").replace("_", " ")}</span>;
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border p-5" style={{ borderColor: `${NAVY}22` }}>
      <div className="text-sm font-bold tracking-wide" style={{ color: NAVY }}>{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
function Loading() { return <div className="p-10 text-center text-[#5C564D]">Loading…</div>; }
function EmptyCard({ title, body }) {
  return (
    <div className="rounded-2xl bg-white border p-10 text-center" style={{ borderColor: `${NAVY}22` }}>
      <Briefcase size={28} weight="duotone" style={{ color: NAVY }} className="mx-auto" />
      <div className="font-heading text-2xl mt-2" style={{ color: NAVY }}>{title}</div>
      <p className="text-sm text-[#5C564D] mt-1">{body}</p>
    </div>
  );
}
function Modal({ onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#1A2432]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="rounded-2xl bg-white max-w-xl w-full p-6 my-8 relative" data-testid={testid} style={{ border: `1px solid ${NAVY}22` }}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-[#F2EBE5]" data-testid="modal-close"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}
function Input({ lab, val, on, type = "text", required, min, testid }) {
  return (
    <label className="block text-xs">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <input data-testid={testid} type={type} required={required} min={min} value={val ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: `${NAVY}22` }} />
    </label>
  );
}
function Select({ lab, val, on, options, required, testid }) {
  return (
    <label className="block text-xs">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <select data-testid={testid} required={required} value={val ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: `${NAVY}22` }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
function TextArea({ lab, val, on, rows = 3, testid }) {
  return (
    <label className="block text-xs sm:col-span-2">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <textarea data-testid={testid} rows={rows} value={val ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: `${NAVY}22` }} />
    </label>
  );
}
function CSV({ lab, val, on, testid }) {
  return (
    <label className="block text-xs mt-2">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <input data-testid={testid} value={val} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: `${NAVY}22` }} />
    </label>
  );
}
function TagMulti({ lab, options, value, onChange, testid }) {
  const toggle = (o) => { const arr = value || []; onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]); };
  return (
    <div className="text-xs">
      {lab && <div className="text-[#5C564D] mb-1">{lab}</div>}
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {options.map((o) => {
          const on = (value || []).includes(o);
          return (
            <button type="button" key={o} onClick={() => toggle(o)} data-testid={`${testid}-${o}`}
              className="px-2.5 py-1 rounded-full text-xs font-semibold border"
              style={{ background: on ? NAVY : "white", color: on ? "white" : "#5C564D", borderColor: on ? NAVY : `${NAVY}22` }}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
function ActionBtn({ on, label: l, color = NAVY, testid }) {
  return <button onClick={on} data-testid={testid} className="text-xs rounded-full px-2.5 py-1 border" style={{ borderColor: color, color }}>{l}</button>;
}
function RepeatList({ items, setItems, fields, testid }) {
  const add = () => setItems([...(items || []), {}]);
  const upd = (i, k, v) => setItems(items.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const del = (i) => setItems(items.filter((_, j) => j !== i));
  return (
    <div className="space-y-2" data-testid={`${testid}-list`}>
      {(items || []).map((row, i) => (
        <div key={i} className="rounded-xl border p-3 relative" style={{ borderColor: `${NAVY}22` }} data-testid={`${testid}-row-${i}`}>
          <button type="button" onClick={() => del(i)} data-testid={`${testid}-del-${i}`} className="absolute top-2 right-2 p-1 rounded-full hover:bg-[#F2EBE5]"><Trash size={12} /></button>
          <div className="grid sm:grid-cols-2 gap-2">
            {fields.map((f) => f.textarea ? (
              <label key={f.k} className="block text-xs sm:col-span-2">
                <div className="text-[#5C564D] mb-1">{f.lab}</div>
                <textarea data-testid={`${testid}-${f.k}-${i}`} rows={2} value={row[f.k] || ""} onChange={(e) => upd(i, f.k, e.target.value)} className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: `${NAVY}22` }} />
              </label>
            ) : f.select ? (
              <label key={f.k} className="block text-xs">
                <div className="text-[#5C564D] mb-1">{f.lab}</div>
                <select data-testid={`${testid}-${f.k}-${i}`} value={row[f.k] || ""} onChange={(e) => upd(i, f.k, e.target.value)} className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: `${NAVY}22` }}>
                  <option value="">—</option>
                  {f.select.map((o) => <option key={o} value={o.toLowerCase()}>{o}</option>)}
                </select>
              </label>
            ) : (
              <label key={f.k} className="block text-xs">
                <div className="text-[#5C564D] mb-1">{f.lab}</div>
                <input data-testid={`${testid}-${f.k}-${i}`} value={row[f.k] || ""} onChange={(e) => upd(i, f.k, e.target.value)} className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: `${NAVY}22` }} />
              </label>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`${testid}-add`} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: NAVY, color: NAVY }}><Plus size={11} weight="bold" className="inline" /> Add</button>
    </div>
  );
}

function splitCsv(s) { return (s || "").split(",").map((x) => x.trim()).filter(Boolean); }
function numOrNull(v) { if (v === "" || v === null || v === undefined) return null; const n = Number(v); return Number.isNaN(n) ? null : n; }
